/*
 * Developed by CQ Inversiones SAS.  Copyright (c) 2024. All rights reserved.
 * Desarrollado por CQ Inversiones SAS.  Copyright (c) 2024. Todos los derechos reservados.
 */

import {Injectable} from '@angular/core';
import {HttpResponse} from '@angular/common/http';
import {map, Observable} from 'rxjs';
import {HTTP_CODE_SUCCESS} from '../../../constants';
import {
  ContractFormInterface,
  ContractListInterface,
  ContractSummaryInterface,
  ContractSummaryParamInterface,
} from '@shared/interface';
import {MkadBaseService, URLS_SERVICE} from '@shared/services';

/**
 * service that handles contract endpoints
 * @extends MkadBaseService
 */
@Injectable()
export class MkadContractsService extends MkadBaseService {
  readonly contract = URLS_SERVICE.meerkad.contract;
  readonly customer = URLS_SERVICE.meerkad.customer;

  /**
   * @description
   * Method that queries the summary list of contracts
   */
  summary(param: ContractSummaryParamInterface): Observable<ContractSummaryInterface[]> {
    return this.http.post<ContractSummaryInterface[]>(this.customer.base + this.contract.base + this.contract.summary, param);
  }

  /**
   * @description
   * Method that queries the nested list of contracts
   */
  list(): Observable<ContractListInterface[]> {
    return this.http.post<ContractListInterface[]>(this.customer.base + this.contract.base + this.contract.list);
  }

  /**
   * @description
   * Method that sends a request to create a contract
   * @param payload payload for the endpoint
   */
  create(payload: ContractFormInterface): Observable<ContractFormInterface> {
    return this.http.post<ContractFormInterface>(this.customer.base + this.contract.base + this.contract.create, payload);
  }

  /**
   * @description
   * Method that sends a request to update a contract
   * @param payload payload for the endpoint
   */
  update(payload: ContractFormInterface): Observable<ContractFormInterface> {
    return this.http.post<ContractFormInterface>(this.customer.base + this.contract.base + this.contract.update, payload);
  }

  /**
   * @description
   * Method that sends a request to delete a contract
   * @param payload payload for the endpoint
   */
  delete(payload: number): Observable<boolean> {
    return this.http.post<HttpResponse<boolean>>(this.customer.base + this.contract.base + this.contract.delete, {id: payload}).pipe(
      map((prevResponse) => {
          if (!prevResponse) return true;
          return prevResponse.status === HTTP_CODE_SUCCESS;
        }
      )
    );
  }

  /**
   * @description
   * Method that sends a request to obtain the data of a contract
   * @param id contract id
   */
  getById(id: number): Observable<ContractFormInterface> {
    return this.http.post<ContractFormInterface>(this.customer.base + this.contract.base + this.contract.get, {id});
  }
}
